/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type { ComponentProps } from 'react';
import { Navigate, Route, Routes } from 'react-router';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

/* Import components */
import LoginPage from '@/components/tabs/auth/LoginPage';
import BaseLoadDetailTab from '@/components/tabs/dashboard/BaseLoadDetailTab';
import HeatingEnergyDetailTab from '@/components/tabs/dashboard/HeatingEnergyDetailTab';
import OverviewTab from '@/components/tabs/dashboard/OverviewTab';
import PvPredictionDetailTab from '@/components/tabs/dashboard/PvPredictionDetailTab';
import StatisticsTab from '@/components/tabs/dashboard/StatisticsTab';
import ThermalConflictTab from '@/components/tabs/dashboard/ThermalConflictTab';
import Api2DataLayoutsTab from '@/components/tabs/layouts/Api2DataLayoutsTab';
import DashboardGridLayoutsTab from '@/components/tabs/layouts/DashboardGridLayoutsTab';
import IconsLayoutsTab from '@/components/tabs/layouts/IconsLayoutsTab';
import KpiApi2LastValueLayoutsTab from '@/components/tabs/layouts/KpiApi2LastValueLayoutsTab';
import KpiApi2TimeSeriesLayoutsTab from '@/components/tabs/layouts/KpiApi2TimeSeriesLayoutsTab';
import KpiStaticLayoutsTab from '@/components/tabs/layouts/KpiStaticLayoutsTab';
import LayoutsTab from '@/components/tabs/layouts/LayoutsTab';
import MarkdownLayoutsTab from '@/components/tabs/layouts/MarkdownLayoutsTab';
import MermaidDiagramLayoutsTab from '@/components/tabs/layouts/MermaidDiagramLayoutsTab';
import StoredPlotlyPlotLayoutsTab from '@/components/tabs/layouts/StoredPlotlyPlotLayoutsTab';
import TimeSeriesPlotLayoutsTab from '@/components/tabs/layouts/TimeSeriesPlotLayoutsTab';
import TypographyLayoutsTab from '@/components/tabs/layouts/TypographyLayoutsTab';
import BaseLoadOverviewTab from '@/components/tabs/overview/anomaly/BaseLoadOverviewTab';
import HeatingEnergyOverviewTab from '@/components/tabs/overview/anomaly/HeatingEnergyOverviewTab';
import PvPredictionOverviewTab from '@/components/tabs/overview/anomaly/PvPredictionOverviewTab';
import WaterLossOverviewTab from '@/components/tabs/overview/anomaly/WaterLossOverviewTab';
import AnomaliesTab from '@/components/tabs/overview/navigation/AnomaliesTab';
import ClientDetailTab from '@/components/tabs/overview/navigation/ClientDetailTab';
import ClientsTab from '@/components/tabs/overview/navigation/ClientsTab';
import StatusDetailTab from '@/components/tabs/overview/navigation/StatusDetailTab';
import StatusesTab from '@/components/tabs/overview/navigation/StatusesTab';
import SystemDetailTab from '@/components/tabs/overview/navigation/SystemDetailTab';
import SystemMetricsDetailTab from '@/components/tabs/overview/navigation/SystemMetricsDetailTab';
import SystemStatisticsTab from '@/components/tabs/overview/navigation/SystemStatisticsTab';
import WaterLossDetailTab from '@/components/tabs/dashboard/WaterLossDetailTab';
import SystemsTab from '@/components/tabs/overview/navigation/SystemsTab';
import ApiReferenceTab from '@/components/tabs/demo/ApiReferenceTab';
import AllConfigurationsTab from '@/components/tabs/demo/AllConfigurationsTab';
import ChartDefinitionsTab from '@/components/tabs/demo/ChartDefinitionsTab';
import BuildingStatisticsTab from '@/components/tabs/demo/BuildingStatisticsTab';
import ComponentOverviewTab from '@/components/tabs/demo/ComponentOverviewTab';
import ComponentsLayoutsTab from '@/components/tabs/layouts/ComponentsLayoutsTab';
import ConfigurationTab from '@/components/tabs/demo/ConfigurationTab';
import DebugOverviewTab from '@/components/tabs/debug/DebugOverviewTab';
import ErrorLogTab from '@/components/tabs/debug/ErrorLogTab';
import DemoTab from '@/components/tabs/demo/DemoTab';
import DeviceDefinitionsTab from '@/components/tabs/demo/DeviceDefinitionsTab';
import ExampleDashboardsTab from '@/components/tabs/demo/ExampleDashboardsTab';
import SensorsDevicesTab from '@/components/tabs/demo/SensorsDevicesTab';
import StaticRendererDemoTab from '@/components/tabs/demo/StaticRendererDemoTab';
import TranslationsTab from '@/components/tabs/demo/TranslationsTab';
import ToolsTab from '@/components/tabs/demo/ToolsTab';
import ContactTab from '@/components/tabs/pages/ContactTab';
import CurrentValueTab from '@/components/tabs/pages/CurrentValueTab';
import DebugTab from '@/components/tabs/pages/DebugTab';
import HomeTab from '@/components/tabs/pages/HomeTab';
import ImprintTab from '@/components/tabs/pages/ImprintTab';
import IntervalValueTab from '@/components/tabs/pages/IntervalValueTab';
import KpisTab from '@/components/tabs/pages/KpisTab';
import LoginTab from '@/components/tabs/pages/LoginTab';
import PlotlyTab from '@/components/tabs/pages/PlotlyTab';
import PriceTrafficLightTab from '@/components/tabs/pages/PriceTrafficLightTab';
import TimeSeriesTab from '@/components/tabs/pages/TimeSeriesTab';
import VersionsTab from '@/components/tabs/pages/VersionsTab';

interface AppRoutesProps {
    currentValueTabProps: ComponentProps<typeof CurrentValueTab>;
    debugEntries: ComponentProps<typeof DebugTab>['entries'];
    hasSession: boolean;
    intervalValueTabProps: ComponentProps<typeof IntervalValueTab>;
    loginPageProps: ComponentProps<typeof LoginPage>;
    loginTabProps: ComponentProps<typeof LoginTab>;
    plotlyTabProps: ComponentProps<typeof PlotlyTab>;
    theme: ComponentProps<typeof ApiReferenceTab>['theme'];
    timeSeriesTabProps: ComponentProps<typeof TimeSeriesTab>;
    toDebugString: ComponentProps<typeof DebugTab>['toDebugString'];
}

export default function AppRoutes({
    currentValueTabProps,
    debugEntries,
    hasSession,
    intervalValueTabProps,
    loginPageProps,
    loginTabProps,
    plotlyTabProps,
    theme,
    timeSeriesTabProps,
    toDebugString,
}: AppRoutesProps) {
    return (
        <Routes>
            <Route
                path="/"
                element={
                    <Navigate
                        to={hasSession ? APP_ROUTES.home : APP_ROUTES.login}
                        replace
                    />
                }
            />
            <Route path={APP_ROUTES.home} element={<HomeTab />} />
            <Route path={APP_ROUTES.demo} element={<DemoTab />} />
            <Route path={APP_ROUTES.tools} element={<ToolsTab />} />
            <Route path={APP_ROUTES.configuration} element={<ConfigurationTab />} />
            <Route path={APP_ROUTES.allconfigurations} element={<AllConfigurationsTab theme={theme} />} />
            <Route path={APP_ROUTES.buildingstatistics} element={<BuildingStatisticsTab />} />
            <Route
                path={APP_ROUTES.markdown}
                element={<StaticRendererDemoTab key="markdown" renderer="markdown" theme={theme} />}
            />
            <Route
                path={APP_ROUTES.mermaid}
                element={<StaticRendererDemoTab key="mermaid" renderer="mermaid" theme={theme} />}
            />
            <Route
                path={APP_ROUTES.plotlyjson}
                element={<StaticRendererDemoTab key="plotly" renderer="plotly" theme={theme} />}
            />
            <Route path={APP_ROUTES.api} element={<ApiReferenceTab theme={theme} />} />
            <Route path={APP_ROUTES.componentoverview} element={<ComponentOverviewTab theme={theme} />} />
            <Route path={APP_ROUTES.debugoverview} element={<DebugOverviewTab />} />
            <Route path={APP_ROUTES.errorlog} element={<ErrorLogTab />} />
            <Route path={APP_ROUTES.exampledashboards} element={<ExampleDashboardsTab />} />
            <Route path={APP_ROUTES.overview} element={<OverviewTab />} />
            <Route path={APP_ROUTES.statistics} element={<StatisticsTab />} />
            <Route path={APP_ROUTES.anomalies} element={<AnomaliesTab />} />
            <Route path={APP_ROUTES.statuses} element={<StatusesTab />} />
            <Route path={`${APP_ROUTES.statuses}/:statusKey`} element={<StatusDetailTab />} />
            <Route path={APP_ROUTES.systems} element={<SystemsTab />} />
            <Route path={`${APP_ROUTES.systems}/:systemKey/pv-prediction/:pvKey`} element={<PvPredictionDetailTab />} />
            <Route path={`${APP_ROUTES.systems}/:systemKey/base-load/:baseLoadKey`} element={<BaseLoadDetailTab />} />
            <Route path={`${APP_ROUTES.systems}/:systemKey/heating-energy/:heatingEnergyKey`} element={<HeatingEnergyDetailTab />} />
            <Route path={`${APP_ROUTES.systems}/:systemKey/water/:waterKey`} element={<WaterLossDetailTab />} />
            <Route path={`${APP_ROUTES.systems}/:systemKey/detail`} element={<SystemMetricsDetailTab />} />
            <Route path={`${APP_ROUTES.systems}/:systemKey/price-traffic-light`} element={<PriceTrafficLightTab />} />
            <Route path={`${APP_ROUTES.systems}/:systemKey/statistics`} element={<SystemStatisticsTab />} />
            <Route path={`${APP_ROUTES.systems}/:systemKey`} element={<SystemDetailTab />} />
            <Route path={APP_ROUTES.clients} element={<ClientsTab />} />
            <Route path={`${APP_ROUTES.clients}/:clientKey`} element={<ClientDetailTab />} />
            <Route path={APP_ROUTES.pricetrafficlight} element={<PriceTrafficLightTab />} />
            <Route
                path={APP_ROUTES.pvprediction}
                element={<PvPredictionOverviewTab />}
            />
            <Route path={`${APP_ROUTES.pvprediction}/:pvKey`} element={<PvPredictionDetailTab />} />
            <Route path={APP_ROUTES.waterloss} element={<WaterLossOverviewTab />} />
            <Route path={`${APP_ROUTES.waterloss}/:waterKey`} element={<WaterLossDetailTab />} />
            <Route
                path={APP_ROUTES.baseload}
                element={<BaseLoadOverviewTab />}
            />
            <Route path={`${APP_ROUTES.baseload}/:baseLoadKey`} element={<BaseLoadDetailTab />} />
            <Route
                path={APP_ROUTES.heatingenergy}
                element={<HeatingEnergyOverviewTab />}
            />
            <Route path={`${APP_ROUTES.heatingenergy}/:heatingEnergyKey`} element={<HeatingEnergyDetailTab />} />
            <Route path={APP_ROUTES.thermalconflict} element={<ThermalConflictTab />} />
            <Route path={APP_ROUTES.contact} element={<ContactTab />} />
            <Route path={APP_ROUTES.imprint} element={<ImprintTab />} />
            <Route path={APP_ROUTES.versions} element={<VersionsTab hasSession={hasSession} />} />
            <Route path={APP_ROUTES.layouts} element={<LayoutsTab />} />
            <Route
                path={`${APP_ROUTES.layouts}/dashboard-grid`}
                element={<DashboardGridLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/time-series-value`}
                element={<Navigate to={`${APP_ROUTES.layouts}/kpi-static`} replace />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/kpi-static`}
                element={<KpiStaticLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/kpi-api2-time-series`}
                element={<KpiApi2TimeSeriesLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/kpi-api2-last-value`}
                element={<KpiApi2LastValueLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/api-data`}
                element={<Api2DataLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/time-series-plot`}
                element={<TimeSeriesPlotLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/stored-plotly-plot`}
                element={<StoredPlotlyPlotLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/mermaid-diagram`}
                element={<MermaidDiagramLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/markdown`}
                element={<MarkdownLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/typography`}
                element={<TypographyLayoutsTab theme={theme} />}
            />
            <Route
                path={`${APP_ROUTES.layouts}/components`}
                element={<ComponentsLayoutsTab theme={theme} />}
            />
            <Route path={`${APP_ROUTES.layouts}/icons`} element={<IconsLayoutsTab theme={theme} />} />
            <Route path={APP_ROUTES.kpis} element={<KpisTab />} />
            <Route path={APP_ROUTES.sensorsdevices} element={<SensorsDevicesTab />} />
            <Route path={APP_ROUTES.devicedefinitions} element={<DeviceDefinitionsTab theme={theme} />} />
            <Route path={APP_ROUTES.translations} element={<TranslationsTab />} />
            <Route
                path={APP_ROUTES.login}
                element={
                    <LoginPage {...loginPageProps} />
                }
            />
            <Route
                path={APP_ROUTES.logindemo}
                element={
                    <LoginTab {...loginTabProps} />
                }
            />
            <Route
                path={APP_ROUTES.plotly}
                element={
                    <PlotlyTab {...plotlyTabProps} />
                }
            />
            <Route path={APP_ROUTES.chartdefinitions} element={<ChartDefinitionsTab />} />
            <Route
                path={APP_ROUTES.timeseries}
                element={
                    <TimeSeriesTab {...timeSeriesTabProps} />
                }
            />
            <Route
                path={APP_ROUTES.currentvalue}
                element={
                    <CurrentValueTab {...currentValueTabProps} />
                }
            />
            <Route
                path={APP_ROUTES.intervalvalues}
                element={
                    <IntervalValueTab {...intervalValueTabProps} />
                }
            />
            <Route
                path={APP_ROUTES.debug}
                element={<DebugTab entries={debugEntries} toDebugString={toDebugString} />}
            />
            <Route
                path="*"
                element={
                    <Navigate
                        to={hasSession ? APP_ROUTES.home : APP_ROUTES.login}
                        replace
                    />
                }
            />
        </Routes>
    );
}
